import React, { useEffect, useState } from 'react';
import { useClassName, useBannerVisibility } from 'common/hooks';
import { ContentfulRichText } from 'common/components';
import { ScrollDownArrow, LogoOverlay } from './HeroOverlays';
import useCustomStyle from '../../hooks';
import HomepageAsset from 'public/screens/Home/Components/Asset';
import HomepageImage from 'public/screens/Home/Components/Image';
import Actions from '../Actions';
import Link from '../Link';

import './hero.less';
import { getStylesByPrefix } from '../../utils';

function usePrefersReducedMotion() {
  const getPreference = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const [prefersReducedMotion, setPrefersReducedMotion] =
    useState(getPreference);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (event) => setPrefersReducedMotion(event.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  return prefersReducedMotion;
}

export default function Hero({ component, refs }) {
  const classNames = useClassName('CLPHero');
  const isBannerVisible = useBannerVisibility();
  const prefersReducedMotion = usePrefersReducedMotion();

  const [, styles] = useCustomStyle(component.fields.settings);

  const withOverlay = Boolean(
    component?.fields?.title ||
      component.fields.title ||
      component?.fields?.links?.length
  );
  const useFullScreen = styles['--full-screen-hero'];
  const showOverlayGradient = styles['--show-overlay-gradient'] === 'true';
  const hideArrow = styles['--hide-arrow-hero'];
  const logoOverlay = component?.fields?.logoOverlay;
  const isBundlesHero = styles['--hero-variant'] === 'bundles';
  const isBundlesFinalCta = styles['--hero-variant'] === 'bundles-cta';
  const badge = component?.fields?.badges?.[0];
  const linkSeparator = component?.fields?.subtitle;
  const customProperties = Object.fromEntries(
    Object.entries(styles).filter(
      ([key, value]) =>
        key.startsWith('--') &&
        (typeof value === 'string' || typeof value === 'number')
    )
  );

  const renderBadge = () =>
    badge?.fields?.label ? (
      <div className={classNames('badge')}>
        {component?.fields?.icon && (
          <HomepageImage
            field={component.fields.icon}
            className={classNames('badge-icon')}
          />
        )}
        <span>{badge.fields.label}</span>
      </div>
    ) : null;

  return (
    <div
      className={`${classNames([
        'container',
        ((withOverlay && !component?.fields?.hideLinearGradient) ||
          showOverlayGradient) &&
          'overlay',
      ])} ${isBundlesHero ? classNames('container', 'bundles') : ''} ${
        isBundlesFinalCta ? classNames('container', 'bundles-cta') : ''
      }`}
      style={{
        ...customProperties,
        ...getStylesByPrefix('--container-', styles),
        ...(styles?.['--overlay-background'] && {
          '--overlay-background': styles['--overlay-background'],
        }),
        ...(styles?.['--overlay-mix-blend-mode'] && {
          '--overlay-mix-blend-mode': styles['--overlay-mix-blend-mode'],
        }),
        ...(styles?.['--overlay-height'] && {
          '--overlay-height': styles['--overlay-height'],
        }),
      }}>
      <HomepageAsset
        asset={component.fields.asset}
        className={classNames('media')}
        {...(isBundlesHero && {
          videoProps: {
            autoplay: !prefersReducedMotion,
            loop: !prefersReducedMotion,
            muted: true,
            playsInline: true,
            preload: 'metadata',
          },
          videoEvents: { autoplay: !prefersReducedMotion },
        })}
        style={{
          opacity: 1,
          aspectRatio: useFullScreen ? 'unset' : undefined,
          height: useFullScreen
            ? `calc(100dvh - 60px - ${isBannerVisible ? '34px' : '0px'})`
            : undefined,
          ...(isBundlesHero &&
            styles['--hero-bundles-object-position'] && {
              objectPosition: styles['--hero-bundles-object-position'],
            }),
          ...getStylesByPrefix('--hero-asset-', styles),
        }}
      />
      <ScrollDownArrow useFullScreen={useFullScreen && !hideArrow} />
      <LogoOverlay logo={logoOverlay} />

      {withOverlay && (
        <div
          className={classNames('content')}
          style={getStylesByPrefix('--content-', styles)}>
          {isBundlesHero && renderBadge()}
          {Boolean(component?.fields?.title) && (
            <h1
              style={Object.fromEntries(
                Object.entries(styles)
                  .filter(([key]) => key.startsWith('--title'))
                  .map(([key, value]) => [key.replace('--title-', ''), value])
              )}>
              {component.fields.title}
            </h1>
          )}
          {Boolean(component?.fields?.description) && (
            <h2
              style={Object.fromEntries(
                Object.entries(styles)
                  .filter(([key]) => key.startsWith('--description'))
                  .map(([key, value]) => [
                    key.replace('--description-', ''),
                    value,
                  ])
              )}>
              <ContentfulRichText
                field={{ 'en-US': component.fields.description }}
              />
            </h2>
          )}
          {isBundlesFinalCta && renderBadge()}
          {Boolean(component?.fields?.links?.length) && (
            <Actions style={getStylesByPrefix('--actions-', styles)}>
              {component.fields.links.map((link, index) => (
                <React.Fragment key={link.sys.id}>
                  {isBundlesHero && index === 1 && linkSeparator && (
                    <span className={classNames('link-separator')}>
                      {linkSeparator}
                    </span>
                  )}
                  <Link component={link} refs={refs} />
                </React.Fragment>
              ))}
            </Actions>
          )}
        </div>
      )}
    </div>
  );
}
